import React from 'react';

import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';

export type GuidePrinciple = Readonly<{
  id: string;
  number: number;
  title: string;
  body: string;
}>;

type GuidePrinciplesGridProps = Readonly<{
  principles: readonly GuidePrinciple[];
  /** Fill down each column before wrapping, instead of left-to-right by row. */
  columnFlow?: boolean;
  /** Max columns once the container passes 600px (three columns from 900px). */
  columns?: 1 | 2 | 3;
  /** Render each principle in a bordered card. */
  card?: boolean;
  /** Render the number as a brand-blue chip beside the title instead of inline text. */
  numberChip?: boolean;
  /** From 600px, size columns as if in a two-column grid of this container width. */
  matchTwoColumnWidth?: string;
}>;

export const GuidePrinciplesGrid: React.FC<GuidePrinciplesGridProps> = ({
  principles,
  columnFlow = false,
  columns = 2,
  card = false,
  numberChip = false,
  matchTwoColumnWidth,
}) => (
  <Box
    sx={gridSx(
      columns,
      columnFlow ? Math.ceil(principles.length / 2) : undefined,
      matchTwoColumnWidth,
    )}
  >
    {principles.map((principle) => (
      <Box key={principle.id} sx={principleSx(card)}>
        {numberChip ? (
          <Box sx={titleRowSx}>
            <Chip
              variant="badge"
              size="sm"
              type="default"
              label={String(principle.number)}
              sx={numberChipSx}
            />
            <Typography variant="text-md-semibold" sx={titleSx}>
              {principle.title}
            </Typography>
          </Box>
        ) : (
          <Typography variant="text-md-semibold" sx={titleSx}>
            {principle.number}. {principle.title}
          </Typography>
        )}
        <Typography variant="text-sm-regular" sx={bodySx}>
          {principle.body}
        </Typography>
      </Box>
    ))}
  </Box>
);

const gridSx =
  (columns: 1 | 2 | 3, rows?: number, matchTwoColumnWidth?: string): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: backrs.spacing['spacing-3xl'],
    ...(columns >= 2 && {
      '@container (min-width: 600px)': {
        gridTemplateColumns: matchTwoColumnWidth
          ? `repeat(2, calc((${matchTwoColumnWidth} - ${backrs.spacing['spacing-3xl']}) / 2))`
          : '1fr 1fr',
        ...(rows ? { gridTemplateRows: `repeat(${rows}, auto)`, gridAutoFlow: 'column' } : {}),
      },
    }),
    ...(columns === 3 && {
      '@container (min-width: 900px)': {
        gridTemplateColumns: matchTwoColumnWidth
          ? `repeat(3, calc((${matchTwoColumnWidth} - ${backrs.spacing['spacing-3xl']}) / 2))`
          : '1fr 1fr 1fr',
      },
    }),
  });

const principleSx =
  (card: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-sm'],
    ...(card && {
      p: backrs.spacing['spacing-2xl'],
      borderRadius: backrs.radius['radius-xl'],
      border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
      backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    }),
  });

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const numberChipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Brand/50'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Brand/700'],
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
